import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ToasterConfig,
  ToasterType,
  ToasterPosition,
  ToasterAction,
} from '../../shared/components/loader/loader.component';

@Injectable({
  providedIn: 'root',
})
export class ToasterService {
  private toastersSubject = new BehaviorSubject<ToasterConfig[]>([]);
  public toasters$: Observable<ToasterConfig[]> =
    this.toastersSubject.asObservable();

  private idCounter = 0;

  show(config: Omit<ToasterConfig, 'id'>): string {
    const id = `toaster-${++this.idCounter}-${Date.now()}`;
    const toaster: ToasterConfig = {
      id,
      duration: 5000,
      position: 'bottom-right',
      closable: true,
      ...config,
    };

    const currentToasters = this.toastersSubject.value;
    this.toastersSubject.next([...currentToasters, toaster]);

    return id;
  }

  success(message: string, title: string = 'Success', duration?: number): string {
    return this.show({
      type: 'success',
      message,
      title,
      duration: duration ?? 5000,
    });
  }

  error(message: string, title: string = 'Error', duration?: number): string {
    return this.show({
      type: 'error',
      message,
      title,
      duration: duration ?? 7000,
    });
  }

  warning(message: string, title: string = 'Warning', duration?: number): string {
    return this.show({
      type: 'warning',
      message,
      title,
      duration: duration ?? 5000,
    });
  }

  info(message: string, title: string = 'Info', duration?: number): string {
    return this.show({
      type: 'info',
      message,
      title,
      duration: duration ?? 5000,
    });
  }

  showWithAction(
    type: ToasterType,
    message: string,
    action: ToasterAction,
    title?: string,
    duration?: number
  ): string {
    return this.show({
      type,
      message,
      title,
      action,
      duration: duration ?? 0,
    });
  }

  remove(id: string): void {
    const currentToasters = this.toastersSubject.value;
    this.toastersSubject.next(currentToasters.filter((t) => t.id !== id));
  }

  removeAll(): void {
    this.toastersSubject.next([]);
  }

  removeByType(type: ToasterType): void {
    const currentToasters = this.toastersSubject.value;
    this.toastersSubject.next(currentToasters.filter((t) => t.type !== type));
  }
}
